import { ReactElement } from 'react'

import { useTranslation } from 'react-i18next'
import { Navigate, Route, useLocation } from 'react-router-dom'
import { publicAppConfigs } from 'shared/configs/publicAppConfigs'
import { DEFAULT_LANGUAGE, LANGUAGES } from 'shared/constants/Languages'

import { DeferredRedirect } from '../DeferredRedirect'
import { PublicRouteLayout } from '../PublicRouteLayout'

const languageCodes = LANGUAGES.map(l => l.value)

const getCurrentLanguage = (i18nLanguage: string | undefined) => {
  const code = i18nLanguage || DEFAULT_LANGUAGE
  const match = LANGUAGES.find(
    l =>
      l.value === code ||
      l.alternativeValue === code ||
      (code && code.startsWith(`${l.value}-`))
  )
  return match?.value || DEFAULT_LANGUAGE
}

export const usePublicRoutes = (isLoggedIn: boolean): ReactElement[] => {
  const location = useLocation()
  const { i18n } = useTranslation()

  return Object.values(publicAppConfigs).flatMap(config => {
    if (config.options?.isLocalizedRoute) {
      const routePath = config.routeProps.exact
        ? `/public${config.path}`
        : `/public${config.path}/*`

      return [
        <Route
          key={`${config.id}-redirect`}
          path={routePath}
          element={
            <DeferredRedirect
              to={{
                pathname: `/${getCurrentLanguage(i18n.language)}/public${config.path}`,
                search: location.search,
                hash: location.hash
              }}
            />
          }
        />,
        ...languageCodes.map(lang => {
          const langRoutePath = config.routeProps.exact
            ? `/${lang}/public${config.path}`
            : `/${lang}/public${config.path}/*`

          return (
            <Route
              key={`${config.id}-${lang}`}
              path={langRoutePath}
              element={
                config.options?.isHybrid && isLoggedIn ? (
                  <Navigate
                    to={{
                      pathname: location.pathname.replace(
                        `/${lang}/public`,
                        ''
                      ),
                      search: location.search,
                      hash: location.hash
                    }}
                    replace
                  />
                ) : (
                  <PublicRouteLayout>
                    <config.component />
                  </PublicRouteLayout>
                )
              }
            />
          )
        })
      ]
    }

    const routePath = config.routeProps.exact
      ? `/public${config.path}`
      : `/public${config.path}/*`

    return [
      <Route
        key={config.id}
        path={routePath}
        element={
          config.options?.isHybrid && isLoggedIn ? (
            <Navigate
              to={{
                pathname: location.pathname.replace('/public', ''),
                search: location.search,
                hash: location.hash
              }}
              replace
            />
          ) : (
            <PublicRouteLayout>
              <config.component />
            </PublicRouteLayout>
          )
        }
      />
    ]
  })
}
